import { StampCardStatus } from '~/generated/hooks';

export interface TestScenario {
  key: string;
  label: string;
  earnedStamps: number;
  description: string;
}

export const testScenarios: TestScenario[] = [
  {
    key: 'default',
    label: 'Normal Progress',
    earnedStamps: 3,
    description: 'Standard progression with some stamps earned'
  },
  {
    key: 'almost-complete',
    label: 'Almost Complete',
    earnedStamps: 8,
    description: 'Nearly finished stamp card'
  },
  {
    key: 'milestone-ready',
    label: 'Milestone Ready',
    earnedStamps: 5,
    description: 'Ready to claim milestone reward'
  },
  {
    key: 'all-milestones',
    label: 'All Complete',
    earnedStamps: 10,
    description: 'All stamps and milestones completed'
  },
  {
    key: 'near-expiry',
    label: 'Near Expiry',
    earnedStamps: 6,
    description: 'Card nearing expiration date'
  },
  {
    key: 'just-started',
    label: 'Just Started',
    earnedStamps: 1,
    description: 'Recently activated card with minimal progress'
  }
];

/**
 * Interface matching the exact GraphQL StampCardFieldsFragment schema
 */
export interface MockStampCardConfig {
  __typename?: 'StampCard';
  id: string;
  currentStamps: number;
  totalStampsEarned: number;
  totalStampsRequired: number;
  status: StampCardStatus;
  activatedAt: string; // GraphQL 'any' type - ISO string
  expiresAt: string; // GraphQL 'any' type - ISO string
  completedAt?: string | null;
  lastStampAt?: string | null;
  progressPercentage: number;
  daysRemaining: number;
  isNearExpiry: boolean;
  claimedMilestoneCount: number;
  totalMilestoneCount: number;
  metadata?: any | null;
  stamps: MockStampConfig[];
  template: {
    __typename?: 'StampTemplate';
    id: string;
    stampingRules?: Array<{
      __typename?: 'StampingRuleType';
      priority?: number | null;
      action: {
        __typename?: 'ActionConfigType';
        type: string;
        config: any;
        description?: string | null;
        descriptionKey?: string | null;
        descriptionParams?: any | null;
      };
      checkers: Array<{
        __typename?: 'CheckerConfigType';
        type: string;
        config: any;
        description?: string | null;
        descriptionKey?: string | null;
        descriptionParams?: any | null;
      }>;
    }> | null;
  };
}

export const defaultMockStampCardConfig: MockStampCardConfig = {
  __typename: 'StampCard',
  id: 'preview',
  currentStamps: 3,
  totalStampsEarned: 3,
  totalStampsRequired: 10,
  status: StampCardStatus.Active,
  activatedAt: new Date().toISOString(),
  expiresAt: new Date(Date.now() + 1000 * 60 * 60 * 24 * 30).toISOString(),
  completedAt: null,
  lastStampAt: null,
  progressPercentage: 30,
  daysRemaining: 30,
  isNearExpiry: false,
  claimedMilestoneCount: 0,
  totalMilestoneCount: 2,
  metadata: null,
  stamps: [],
  template: {
    __typename: 'StampTemplate',
    id: '1',
    stampingRules: []
  }
};

export interface MockMilestone {
  id: string;
  milestoneStamps: number;
  name: string;
  description: string;
  rewardType: string;
  rewardValue: string;
  autoClaimable: boolean;
  iconUrl: string;
  badgeColor: null;
  displayOrder: number;
  isClaimed: boolean;
  isAvailable: boolean;
  stampsRemaining: number;
  rewardDetail: null;
}

export const createMockMilestone = (index: number, earnedStamps: number): MockMilestone => ({
  id: `milestone-${index}`,
  milestoneStamps: index + 1,
  name: index === 0 ? 'Milestone Reward' : `Next Milestone`,
  description: index === 0 ? 'Test milestone' : 'Next milestone',
  rewardType: 'DISCOUNT',
  rewardValue: index === 0 ? '10' : '20',
  autoClaimable: false,
  iconUrl: '',
  badgeColor: null,
  displayOrder: index,
  isClaimed: (index + 1) <= earnedStamps,
  isAvailable: (index + 1) <= earnedStamps,
  stampsRemaining: Math.max(0, (index + 1) - earnedStamps),
  rewardDetail: null
});

/**
 * Interface matching the exact GraphQL Stamp schema within StampCardFieldsFragment
 */
export interface MockStampConfig {
  __typename?: 'Stamp';
  index: number;
  claimed: boolean;
  containMilestone: boolean;
  currentIcon?: string | null;
  remainingStamps: number;
  milestones: Array<{
    __typename?: 'StampMilestone';
    id: string;
    milestoneStamps: number;
    name: string;
    description?: string | null;
    rewardType: string;
    rewardValue: string;
    autoClaimable: boolean;
    iconUrl?: string | null;
    badgeColor?: string | null;
    displayOrder: number;
    isClaimed?: boolean | null;
    isAvailable?: boolean | null;
    stampsRemaining?: number | null;
    rewardDetail?: {
      __typename?: 'StampMilestoneRewardDetail';
      id?: string | null;
      name?: string | null;
    } | null;
  }>;
}

export const createMockStampConfig = (
  index: number,
  earnedStamps: number,
  totalStamps: number,
  iconUrl: string,
  iconFilledUrl: string,
  iconWithRewardUrl: string,
  iconWithRewardFilledUrl: string,
  milestones: any[] = []
): MockStampConfig => {
  const containMilestone = milestones.length > 0;
  let finalIcon = iconUrl;

  if (containMilestone) {
    const firstMilestone = milestones[0];
    if (firstMilestone?.iconUrl) {
      finalIcon = firstMilestone.iconUrl;
    } else if (index < earnedStamps) {
      finalIcon = iconWithRewardFilledUrl || iconWithRewardUrl || iconFilledUrl;
    } else {
      finalIcon = iconWithRewardUrl || iconUrl;
    }
  } else if (index < earnedStamps) {
    finalIcon = iconFilledUrl || iconUrl;
  }

  return {
    index,
    claimed: index < earnedStamps,
    containMilestone,
    currentIcon: finalIcon,
    remainingStamps: Math.max(0, totalStamps - earnedStamps),
    milestones: milestones.map((m: any, mIndex: number) => ({
      id: `milestone-${m.name || index}`,
      milestoneStamps: m.stampsRequired || m.milestoneStamps || (index + 1),
      name: m.name || `Reward ${index + 1}`,
      description: m.description || '',
      rewardType: m.rewardType || 'DISCOUNT',
      rewardValue: m.rewardValue || '10',
      autoClaimable: m.autoClaimable || false,
      iconUrl: m.iconUrl || '',
      badgeColor: m.badgeColor || null,
      displayOrder: m.displayOrder || 0,
      isClaimed: (m.stampsRequired || m.milestoneStamps || 0) <= earnedStamps,
      isAvailable: (m.stampsRequired || m.milestoneStamps || 0) <= earnedStamps,
      stampsRemaining: Math.max(0, (m.stampsRequired || m.milestoneStamps || 0) - earnedStamps),
      rewardDetail: null
    }))
  };
};

/**
 * Enhanced mock data generator for comprehensive StampCard previews
 */
export interface PreviewConfig {
  totalStamps: number;
  earnedStamps: number;
  iconUrl?: string;
  iconFilledUrl?: string;
  iconWithRewardUrl?: string;
  iconWithRewardFilledUrl?: string;
  milestonePositions?: number[];
  scenario?: string;
  expiryDays?: number;
}

export const generateMockStampCardData = (config: PreviewConfig) => {
  const {
    totalStamps = 10,
    earnedStamps = 0,
    iconUrl = '',
    iconFilledUrl = '',
    iconWithRewardUrl = '',
    iconWithRewardFilledUrl = '',
    milestonePositions = [3, 7], // Default milestone positions
    scenario = 'default',
    expiryDays = 30
  } = config;

  const clampedEarned = Math.max(0, Math.min(earnedStamps, totalStamps));
  const isNearExpiry = expiryDays <= 7;
  const isCompleted = clampedEarned >= totalStamps;
  
  const mockStampCardData: MockStampCardConfig = {
    __typename: 'StampCard',
    id: `preview-${scenario}`,
    currentStamps: clampedEarned,
    totalStampsEarned: clampedEarned,
    totalStampsRequired: totalStamps,
    status: isCompleted ? StampCardStatus.Completed : StampCardStatus.Active,
    activatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * (30 - expiryDays)).toISOString(),
    expiresAt: new Date(Date.now() + 1000 * 60 * 60 * 24 * expiryDays).toISOString(),
    completedAt: isCompleted ? new Date().toISOString() : null,
    lastStampAt: clampedEarned > 0 ? new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString() : null,
    progressPercentage: (clampedEarned / totalStamps) * 100,
    daysRemaining: expiryDays,
    isNearExpiry,
    claimedMilestoneCount: milestonePositions.filter(pos => pos <= clampedEarned).length,
    totalMilestoneCount: milestonePositions.length,
    metadata: null,
    template: {
      __typename: 'StampTemplate',
      id: '1',
      stampingRules: []
    },
    stamps: Array.from({ length: totalStamps }, (_, i) => {
      const containMilestone = milestonePositions.includes(i + 1);
      let finalIcon = iconUrl;
      
      if (containMilestone) {
        if (i < clampedEarned) {
          finalIcon = iconWithRewardFilledUrl || iconWithRewardUrl || iconFilledUrl || iconUrl;
        } else {
          finalIcon = iconWithRewardUrl || iconUrl;
        }
      } else if (i < clampedEarned) {
        finalIcon = iconFilledUrl || iconUrl;
      }

      return {
        __typename: 'Stamp' as const,
        index: i,
        claimed: i < clampedEarned,
        containMilestone,
        currentIcon: finalIcon || null,
        remainingStamps: Math.max(0, totalStamps - clampedEarned),
        milestones: containMilestone ? [{
          __typename: 'StampMilestone' as const,
          id: `milestone-${i}`,
          milestoneStamps: i + 1,
          name: `Milestone ${milestonePositions.indexOf(i + 1) + 1}`,
          description: `Reach ${i + 1} stamps to unlock reward`,
          rewardType: 'DISCOUNT',
          rewardValue: milestonePositions.indexOf(i + 1) === 0 ? '10' : '20',
          autoClaimable: false,
          iconUrl: iconWithRewardUrl || null,
          badgeColor: null,
          displayOrder: milestonePositions.indexOf(i + 1),
          isClaimed: (i + 1) <= clampedEarned,
          isAvailable: (i + 1) <= clampedEarned,
          stampsRemaining: Math.max(0, (i + 1) - clampedEarned),
          rewardDetail: null
        }] : [],
      };
    })
  };

  return mockStampCardData;
};

/**
 * Generate mock data for specific test scenarios
 */
export const generateScenarioData = (
  scenario: TestScenario,
  totalStamps: number = 10,
  iconConfig: {
    iconUrl?: string;
    iconFilledUrl?: string;
    iconWithRewardUrl?: string;
    iconWithRewardFilledUrl?: string;
  } = {}
) => {
  const config: PreviewConfig = {
    totalStamps,
    earnedStamps: scenario.earnedStamps,
    scenario: scenario.key,
    ...iconConfig
  };

  // Adjust config based on scenario
  switch (scenario.key) {
    case 'near-expiry':
      config.expiryDays = 3;
      break;
    case 'just-started':
      config.earnedStamps = 1;
      break;
    case 'almost-complete':
      config.earnedStamps = totalStamps - 1;
      break;
    case 'all-milestones':
      config.earnedStamps = totalStamps;
      break;
    case 'milestone-ready':
      config.earnedStamps = Math.floor(totalStamps / 2);
      break;
  }

  return generateMockStampCardData(config);
};

/**
 * Create dynamic preview data that updates with form changes
 */
export const createDynamicPreviewData = (
  formValues: {
    iconUrl?: string;
    iconFilledUrl?: string;
    iconWithRewardUrl?: string;
    iconWithRewardFilledUrl?: string;
    totalStamps?: number;
    milestonePositions?: number[];
  },
  previewControls: {
    earnedStamps: number;
    showMilestones: boolean;
    scenario: string;
  }
) => {
  const totalStamps = formValues.totalStamps || 10;
  const earnedStamps = Math.min(previewControls.earnedStamps, totalStamps);
  const milestonePositions = previewControls.showMilestones 
    ? (formValues.milestonePositions || [3, 7]) 
    : [];

  return generateMockStampCardData({
    totalStamps,
    earnedStamps,
    iconUrl: formValues.iconUrl,
    iconFilledUrl: formValues.iconFilledUrl,
    iconWithRewardUrl: formValues.iconWithRewardUrl,
    iconWithRewardFilledUrl: formValues.iconWithRewardFilledUrl,
    milestonePositions,
    scenario: previewControls.scenario
  });
};